import React from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, Text, HStack, useStyleConfig, Button} from '@chakra-ui/react'
import Link from '@salesforce/retail-react-app/app/components/link'
import CartItemVariantImage from '../../item-variant/item-image'
import CartItemVariantName from '../../../components/item-variant/item-name'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import VatLabel from '../../vat-label'
import ItemVariantProvider from '@salesforce/retail-react-app/app/components/item-variant'
import {DeleteIcon} from '../../icons'
import {FormattedMessage, FormattedNumber, useIntl} from 'react-intl'
import {remCalc} from '../../../theme/_utils'
import InformativeLabel, {InformativeLabelGroup} from '../../informative-label'
import {productUrlBuilder} from '../../../utils/url'
import {useBooleanCustomPreference} from '../../../hooks/use-custom-preference'

const CartBundle = ({bundleItems, bundleDetails, handleRemoveItem}) => {
    const {currency: activeCurrency} = useCurrency()
    const style = useStyleConfig('Bundle')
    const {formatMessage} = useIntl()
    const isVATFlag = useBooleanCustomPreference('showVATFlag')
    const rawDiscount = bundleDetails?.discountPercentage || '0%'
    const numericDiscount = Math.round(parseFloat(rawDiscount.replace('%', '')))
    const promoLabel = `${formatMessage({
        id: 'item_variant.discountLabel',
        defaultMessage: 'Discount',
    })} ${numericDiscount}%`

    const bundleWeeefee = bundleItems?.reduce((sum, product) => {
        const weeeFeeTotal =
            product?.priceAdjustments?.reduce((acc, adj) => {
                return adj?.promotionId === 'weeeFee' ? acc + (adj.price || 0) : acc
            }, 0) || 0
        return sum + weeeFeeTotal
    }, 0)

    const hasNoDiscount =
        Number(bundleDetails?.totalPrice) === Number(bundleDetails?.totalDiscountedPrice)

    const removeWeeeFeeFromPriceAfterItemDiscount = (product) => {
        if (!product?.priceAfterItemDiscount) return product?.price

        const weeeFeeTotal =
            product?.priceAdjustments?.reduce((sum, adj) => {
                return adj?.promotionId === 'weeeFee' ? sum + (adj.price || 0) : sum
            }, 0) || 0

        return product.priceAfterItemDiscount - weeeFeeTotal
    }

    return (
        <Box {...style.cartBundleContainer}>
            <Flex {...style.cartHeaderContainer}>
                <Flex {...style.cartHeaderInfoContainer}>
                    <Text {...style.cartBundleName}>{bundleDetails?.bundleName}</Text>
                    <Flex {...style.cartHeaderPriceContainer}>
                        <Text data-testid="price">
                            <FormattedNumber
                                style="currency"
                                currency={activeCurrency}
                                value={bundleDetails?.totalDiscountedPrice - bundleWeeefee}
                            />
                        </Text>
                        {!hasNoDiscount && (
                            <>
                                <Text {...style.cartSuggestedPrice}>
                                    <FormattedNumber
                                        style="currency"
                                        currency={activeCurrency}
                                        value={bundleDetails?.totalPrice}
                                    />
                                </Text>
                                <InformativeLabel label={promoLabel} variant="light" />
                            </>
                        )}
                    </Flex>
                </Flex>
                <Button
                    onClick={handleRemoveItem}
                    data-testid="cart-item-remove-button"
                    className="js-personalization-remove-button"
                    data-product-code={bundleItems.map((product) => product.productId).join(',')}
                >
                    <DeleteIcon
                        w="16px"
                        h="16px"
                        data-product-code={bundleItems
                            .map((product) => product.productId)
                            .join(',')}
                    />
                </Button>
            </Flex>

            {bundleItems.map((product, idx) => {
                const cartItemPriceData = {
                    isOnSale: product.priceAfterItemDiscount < product.price,
                    listPrice: product.price,
                    unitPrice: product.priceAfterItemDiscount,
                }
                const productUrl = productUrlBuilder(product)
                return (
                    <ItemVariantProvider variant={product} key={idx}>
                        <Flex {...style.cartBundleItemContainer}>
                            <Link to={productUrl}>
                                <CartItemVariantImage
                                    {...style.cartBundleItemImage}
                                    imageProps={style?.cartImage}
                                />
                            </Link>
                            <Box {...style.cartItemDetailContainer}>
                                <Flex {...style.infoLabelContainer}>
                                    <InformativeLabelGroup
                                        product={product}
                                        priceData={cartItemPriceData}
                                        isBundle={true}
                                    />
                                </Flex>
                                <Box {...style.cartItemHeaderStack}>
                                    <Link to={productUrl}>
                                        <CartItemVariantName {...style.cartItemName} />
                                    </Link>
                                </Box>
                                {cartItemPriceData.unitPrice === 0 ? (
                                    <Box {...style.bundleFreeGiftLabel}>
                                        <FormattedMessage
                                            defaultMessage="Free Gift"
                                            id="bundle_free_gift.text"
                                        />
                                    </Box>
                                ) : (
                                    <Box {...style.cartItemOmniAndPrice}>
                                        <Text>
                                            <FormattedNumber
                                                style="currency"
                                                currency={activeCurrency}
                                                value={removeWeeeFeeFromPriceAfterItemDiscount(
                                                    product,
                                                )}
                                            />
                                        </Text>
                                        {cartItemPriceData.isOnSale && (
                                            <Flex gap={remCalc(8)}>
                                                <Text {...style.cartSuggestedPrice}>
                                                    <FormattedNumber
                                                        style="currency"
                                                        currency={activeCurrency}
                                                        value={product.price}
                                                    />
                                                </Text>
                                                <Text
                                                    {...style.cartSuggestedText}
                                                    color="success.100"
                                                >
                                                    {formatMessage({
                                                        id: 'item_variant.suggested_price',
                                                        defaultMessage: 'Suggested Price',
                                                    })}
                                                </Text>
                                            </Flex>
                                        )}
                                    </Box>
                                )}

                                {isVATFlag && (
                                    <Box {...style.cartItemVatDetails}>
                                        <HStack>
                                            <VatLabel
                                                price={product?.price}
                                                vatRate={product?.c_taxRate}
                                            />
                                        </HStack>
                                    </Box>
                                )}
                            </Box>
                        </Flex>
                    </ItemVariantProvider>
                )
            })}
        </Box>
    )
}

CartBundle.propTypes = {
    bundleItems: PropTypes.arrayOf(PropTypes.object).isRequired,
    bundleDetails: PropTypes.shape({
        totalPrice: PropTypes.number,
        totalDiscountedPrice: PropTypes.number,
        discountPercentage: PropTypes.string,
        bundleName: PropTypes.string,
    }),
    handleRemoveItem: PropTypes.func,
}

export default CartBundle
